import { useParams } from "react-router-dom";
import useSWR from "swr";
import { V1TableListEnvelope, V1TableResult } from "~/types/interfaces/catalogTables";
import { apiClient } from "~/utils/apiClient";

const { VITE_CATALOG_API } = import.meta.env;

/** The backend caps a single page of dataset tables at 100. */
const TABLE_PAGE_SIZE = 100;

/**
 * Every table FQN in a dataset, collected by walking the offset/limit pages that
 * `GET /v1/datasets/{id}/tables` returns and following `metadata.has_more` until the
 * set is exhausted.
 */
export async function fetchAllDatasetTableFQNs(baseUrl: string): Promise<string[]> {
  const fqns: string[] = [];
  let offset = 0;
  for (;;) {
    const page = await apiClient<V1TableListEnvelope<V1TableResult>>(
      `${baseUrl}?limit=${TABLE_PAGE_SIZE}&offset=${offset}`
    );
    fqns.push(...page.items.map((table) => table.fqn));
    offset += page.items.length;
    // Stop when the server reports no more pages, and defensively when a page comes
    // back empty so a misbehaving `has_more` can't spin forever.
    if (!page.metadata.has_more || page.items.length === 0) {
      break;
    }
  }
  return fqns;
}

/**
 * The FQNs of every table associated with a dataset. Requires ANALYZE access to the
 * dataset — a PREVIEW-level caller gets a 403.
 */
const useDatasetTableFQNs = (includeCustomerEnv = true, id?: string) => {
  const { datasetId = id } = useParams();
  const { data, ...rest } = useSWR(
    datasetId ? `${VITE_CATALOG_API}/v1/datasets/${datasetId}/tables` : null,
    fetchAllDatasetTableFQNs
  );

  return {
    data: data?.map((fqn) => {
      if (!includeCustomerEnv) {
        // Strip off prepended `snowflake.<customer_env>.`
        const segments = fqn.split(".");
        return segments.slice(2).join(".");
      }
      return fqn;
    }),
    ...rest,
  };
};

export default useDatasetTableFQNs;
